<template>
    <div class="modal-card">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">Subir mayor</h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>
        <section class="modal-card-body box-body">
            <div class="px-5">
                <!-- Fecha de corte (mayor <-> SII) -->
                <div class="is-size-6 has-text-weight-light mb-3">
                    <strong>Fecha de corte (mayor &harr; SII)</strong>
                    <br />
                    Hasta esta fecha el reporte usa el <strong>mayor</strong>; despu&eacute;s,
                    el <strong>SII</strong> (Fintoc). D&eacute;jala vac&iacute;a para usar solo SII,
                    o usa <strong>Solo mayor</strong> si la empresa no usa SII.
                </div>
                <b-field>
                    <b-datepicker
                        v-model="cutoffDate"
                        placeholder="Selecciona la fecha de corte"
                        icon="calendar-today"
                        locale="es-CL"
                        :editable="true"
                        :loading="loadingCutoff"
                        trap-focus
                        expanded
                    />
                </b-field>
                <p v-if="isLedgerOnly" class="help has-text-info mb-2">
                    Solo mayor: el reporte usa el mayor en todas las fechas.
                </p>
                <div class="buttons are-small is-justify-content-flex-end">
                    <b-button
                        class="is-info is-light"
                        :disabled="isLedgerOnly || savingCutoff"
                        @click="setLedgerOnly()"
                    >
                        Solo mayor
                    </b-button>
                    <b-button
                        class="is-light"
                        :disabled="!cutoffDate || savingCutoff"
                        @click="clearCutoff()"
                    >
                        Limpiar
                    </b-button>
                    <b-button
                        class="is-primary"
                        :disabled="savingCutoff"
                        :loading="savingCutoff"
                        @click="saveCutoff()"
                    >
                        Guardar
                    </b-button>
                </div>

                <div class="divisionLine"></div>

                <!-- Planilla del mayor (.xlsx) -->
                <div class="is-size-6 has-text-weight-light mb-3">
                    <strong>Planilla del mayor (.xlsx)</strong>
                    <br />
                    Arrastra el Excel del libro mayor a esta &aacute;rea.
                </div>
                <UploadFile
                    fileName="planilla del mayor"
                    messageFormat="XLSX"
                    extensionAccept=".xlsx"
                    :isLoading="uploadingLedger"
                    @uploaded="onUploaded"
                    @removedFile="onRemovedFile"
                />
            </div>
        </section>
        <footer class="modal-card-foot bt-none has-background-white is-justify-content-flex-end">
            <span class="help mr-3 mt-2">La importaci&oacute;n estar&aacute; disponible pr&oacute;ximamente.</span>
            <b-button
                class="is-primary"
                aria-label="Subir mayor"
                :disabled="true"
                role="button"
            >
                Subir
            </b-button>
        </footer>
    </div>
</template>

<script>
import UploadFile from "@/components/uploadFile/UploadFile";
import { getLedgerCutoff, setLedgerCutoff } from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";
import moment from "moment";

// Corte centinela "todo mayor": una fecha lejana (regla emitted_at <= corte →
// mayor) hace que la empresa muestre el mayor en todas las fechas sin tener que
// re-actualizar el corte. Empresas que solo usan mayor (sin SII/Fintoc).
const LEDGER_ONLY_CUTOFF = "2099-12-31";

export default {
    name: "UploadLedger",
    components: {
        UploadFile,
    },
    props: {
        idCompany: {
            type: [Number, String],
            required: true,
            default: null,
        },
    },
    data() {
        return {
            cutoffDate: null,
            loadingCutoff: false,
            savingCutoff: false,
            fileLedger: null,
            uploadingLedger: false,
        };
    },
    created() {
        this.loadCutoff();
    },
    computed: {
        isLedgerOnly() {
            return (
                !!this.cutoffDate &&
                moment(this.cutoffDate).format("YYYY-MM-DD") === LEDGER_ONLY_CUTOFF
            );
        },
    },
    methods: {
        loadCutoff() {
            this.loadingCutoff = true;
            getLedgerCutoff(this.idCompany)
                .then(({ data }) => {
                    this.cutoffDate = data.ledger_cutoff_date
                        ? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
                        : null;
                })
                .catch((error) => {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudo leer la fecha de corte del mayor",
                        empty: true,
                    });
                })
                .finally(() => {
                    this.loadingCutoff = false;
                });
        },
        persistCutoff(value) {
            this.savingCutoff = true;
            return setLedgerCutoff(this.idCompany, value)
                .then(({ data }) => {
                    this.cutoffDate = data.ledger_cutoff_date
                        ? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
                        : null;
                    notificate("Fecha de corte del mayor actualizada", "is-success is-light");
                })
                .catch((error) => {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudo guardar la fecha de corte del mayor",
                        empty: true,
                    });
                })
                .finally(() => {
                    this.savingCutoff = false;
                });
        },
        saveCutoff() {
            const value = this.cutoffDate
                ? moment(this.cutoffDate).format("YYYY-MM-DD")
                : null;
            this.persistCutoff(value);
        },
        clearCutoff() {
            this.persistCutoff(null);
        },
        setLedgerOnly() {
            this.cutoffDate = moment(LEDGER_ONLY_CUTOFF, "YYYY-MM-DD").toDate();
            this.persistCutoff(LEDGER_ONLY_CUTOFF);
        },
        onUploaded(file) {
            this.fileLedger = file;
        },
        onRemovedFile() {
            this.fileLedger = null;
        },
    },
};
</script>
